import { PageDetails_page } from "@the-volte/svc-core-sdk/lib/queries/gqlTypes/PageDetails";
import { AttributedProductsView } from "@views/AttributedProducts";
import ContactView from "@views/Contact/ContactView";
import { DesignerIndexView, IndexView } from "@views/Index";
import get from "lodash.get";
import { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { generatePath } from "react-router";

import { siteUrl } from "@app/constants";
import { MetaWrapper, NotFound } from "@components/legacy";
import SearchView from "@pages/search";
import { paths } from "@paths";
import { PriceFilterType } from "@utils/filterData";
import { designerSlugSuffix } from "@utils/menu";
import { FeaturedProducts, PageTypes } from "@utils/ssr";

import { Article_page } from "./gqlTypes/Article";
import Page from "./Page";

export interface ArticleViewProps {
  params: { slug: string; shouldRevalidate?: boolean };
  data: {
    article: PageDetails_page | null;
    featuredProducts: FeaturedProducts;
    presetAttributeFilter: { [key: string]: string[] };
    categoryFilter: { [key: string]: string[] };
    presetPriceFilters: PriceFilterType;
  };
}

const Meta = ({ children, data }) => {
  const meta = {
    description: data.article.seoDescription,
    title: data.article.seoTitle,
    image: "",
  };
  if (data?.attributedProducts?.products?.length) {
    meta.image = data?.designer?.designerProducts[0].thumbnail.url;
  }
  if (data?.featuredProducts?.products?.length) {
    meta.image = data?.featuredProducts?.products[0].thumbnail.url;
  }
  if (data?.other?.products?.length) {
    meta.image = data?.other?.products[0].thumbnail.url;
  }
  return <MetaWrapper meta={meta}>{children}</MetaWrapper>;
};

export const ArticleView: NextPage<ArticleViewProps> = ({
  data,
  params: { slug },
}) => {
  const [canDisplay, headerImage] = useMemo(
    () => [data?.article, data?.featuredProducts?.backgroundImage?.url],
    [data.article]
  );
  const router = useRouter();

  const getBreadcrumbs = (article: Article_page) => [
    {
      link: generatePath(paths.page, { slug: article.slug }),
      value: article.title,
    },
  ];

  const isDesignerPage = data?.article?.pageType?.slug === PageTypes.DESIGNER;

  const canonicalUrl = isDesignerPage
    ? `${siteUrl}${paths.designers}/${slug}${designerSlugSuffix}`
    : `${siteUrl}${router.asPath}`;

  const canonicalRef = data?.article && (
    <Head>
      <link rel="canonical" href={canonicalUrl} />
      <title>{data.article.seoTitle}</title>
    </Head>
  );
  if (isDesignerPage) {
    return (
      <Meta data={data}>
        {canonicalRef}
        <AttributedProductsView
          data={data}
          pagetype={data?.article?.pageType}
        />
      </Meta>
    );
  }

  if (data?.article?.pageType?.slug === PageTypes.FILTER) {
    return (
      <Meta data={data}>
        {canonicalRef}
        <AttributedProductsView
          data={data}
          presetAttributeFilter={data.presetAttributeFilter}
          categoryFilter={data.categoryFilter}
        />
      </Meta>
    );
  }

  if (data?.article?.pageType?.slug === PageTypes.SEARCH) {
    return (
      <Meta data={data}>
        {canonicalRef}
        <SearchView data={data} />
      </Meta>
    );
  }

  if (data?.article?.pageType?.slug === PageTypes.LOCATION) {
    return (
      <Meta data={data}>
        {canonicalRef}
        <AttributedProductsView data={data} presetAttributeFilter={{}} />
      </Meta>
    );
  }

  if (data?.article?.slug === PageTypes.CONTACT) {
    return (
      <Meta data={data}>
        {canonicalRef}
        <ContactView />
      </Meta>
    );
  }

  // designer page
  // hacky fix for now
  // probably refactor the whole page/[slug] into
  // something more modularised for dev and seo friendly
  // this pattern can be bloated very fast and hard to maintain in the future
  // initial loading for affected pages will have a bigger payload as well
  if (
    data?.article?.pageType?.slug === PageTypes.INDEX &&
    get(data, "article.slug") === "designers"
  ) {
    return (
      <Meta data={data}>
        {canonicalRef}
        <DesignerIndexView data={data} />
      </Meta>
    );
  }

  if (data?.article?.pageType?.slug === PageTypes.INDEX) {
    return (
      <Meta data={data}>
        {canonicalRef}
        <IndexView data={data} />
      </Meta>
    );
  }

  return canDisplay ? (
    <Meta data={data}>
      {canonicalRef}
      <Page
        breadcrumbs={getBreadcrumbs(data.article)}
        headerImage={headerImage}
        page={data.article}
      />
    </Meta>
  ) : (
    <NotFound />
  );
};
